<template>
  <div>
    <b-card
      title="Ajouter un membre du personnel"
      aria-label="Formulaire d’ajout de membre du personnel"
    >
      <ValidationObserver
        ref="staffFormObserver"
      >
        <b-form
          ref="staffForm"
          aria-labelledby="staff-form-title"
          role="form"
          @submit.prevent="saveStaff"
        >
        <!-- Titre Visuellement caché pour l’accessibilité -->
        <h1
          id="staff-form-title"
          class="sr-only"
        >
          Formulaire d’ajout de membre du personnel
        </h1>
        <!-- Sélection du rôle (déplacé en haut) -->
        <h5
          id="section-role"
          class="mb-2"
        >
          <feather-icon
            icon="ShieldIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Sélection du rôle
        </h5>

        <b-form-group label-class="form-label-premium"
          label="Rôle professionnel *"
          label-for="role"
          aria-required="true"
          description="Sélectionnez le rôle logistique du membre du personnel."
        >
          <validation-provider
            #default="{ errors, valid }"
            name="Rôle"
            rules="required"
            mode="eager"
          >
            <v-select
              id="role"
              v-model="form.role"
              :options="staffRoles"
              label="label"
              :reduce="role => role.value"
              placeholder="Sélectionnez un rôle professionnel"
              :class="[
                'enhanced-select',
                errors.length > 0 ? 'is-invalid' : '',
                valid && form.role ? 'is-valid' : ''
              ]"
              :clearable="true"
              :searchable="true"
              aria-describedby="role-desc"
              aria-required="true"
              @input="onRoleChange"
            />
            <b-form-text
              id="role-desc"
              class="sr-only"
            >
              Champ obligatoire. Sélectionnez le rôle professionnel du membre du personnel.
            </b-form-text>
            <small
              v-if="errors[0]"
              class="text-danger validation-message-animate"
            >{{ errors[0] }}</small>
          </validation-provider>
        </b-form-group>

        <b-form-group label-class="form-label-premium"
          label="Éligible aux commissions"
          label-for="can_receive_commission"
          class="mb-2"
        >
          <b-form-checkbox
            id="can_receive_commission"
            v-model="form.can_receive_commission"
            @change="onCommissionEligibilityChange"
          >
            Autoriser les commissions par commande
          </b-form-checkbox>
        </b-form-group>

        <div v-if="form.can_receive_commission">
          <h5
            id="section-commission"
            class="mb-2"
          >
            <feather-icon
              icon="PercentIcon"
              class="mr-1"
              aria-hidden="true"
            />
            Paramètres de commission
          </h5>
          <b-row>
            <b-col md="4">
              <b-form-group label-class="form-label-premium"
                label="Commission par commande (DT) *"
                label-for="commission_rate"
                aria-required="true"
              >
                <b-form-input class="premium-input"
                  id="commission_rate"
                  v-model="form.commission_rate"
                  :formatter="formatPrice"
                  type="text"
                  inputmode="decimal"
                  placeholder="0.200"
                  aria-required="true"
                  @keypress="allowOnlyDecimalNumbers"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label-class="form-label-premium"
                label="Date de début *"
                label-for="commission_start_date"
                aria-required="true"
              >
                <b-form-input class="premium-input"
                  id="commission_start_date"
                  v-model="form.commission_start_date"
                  type="date"
                  aria-required="true"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label-class="form-label-premium"
                label="Date de fin *"
                label-for="commission_end_date"
                aria-required="true"
              >
                <b-form-input class="premium-input"
                  id="commission_end_date"
                  v-model="form.commission_end_date"
                  type="date"
                  aria-required="true"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Informations personnelles -->
        <h5
          id="section-infos-personnelles"
          class="mb-2"
        >
          <feather-icon
            icon="UserIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations personnelles
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Nom complet *"
              label-for="username"
              aria-required="true"
              description="Nom et prénom du membre du personnel, tel qu’indiqué sur les documents officiels."
            >
              <b-form-input class="premium-input"
                id="username"
                v-model="form.username"
                :state="nameState"
                placeholder="Entrez le nom complet"
                aria-describedby="username-desc"
                aria-required="true"
                autocomplete="name"
              />
              <b-form-text
                id="username-desc"
                class="sr-only"
              >
                Champ obligatoire. Saisissez le nom et le prénom complets.
              </b-form-text>
              <b-form-invalid-feedback v-if="submitted && !form.username">
                Le nom complet est requis
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Adresse e-mail professionnelle *"
              label-for="email"
              aria-required="true"
              description="Adresse e-mail utilisée pour la connexion et la communication interne."
            >
              <validation-provider
                #default="{ errors, valid }"
                name="Email"
                rules="required|email|asyncEmail"
              >
                <div class="position-relative">
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="email"
                      v-model="form.email"
                      type="email"
                      :state="errors.length > 0 ? false : (valid && form.email ? true : null)"
                      placeholder="prenom.nom@entreprise.fr"
                      aria-describedby="email-desc"
                      aria-required="true"
                      autocomplete="email"
                      class="premium-input"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        icon="RefreshCwIcon"
                        :class="form.username && form.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                        v-b-tooltip.hover="emailGenerationTooltip"
                        @click="form.username && form.username.trim().length > 0 ? generateCompanyEmail() : null"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <feather-icon
                    v-if="valid && form.email"
                    icon="CheckCircleIcon"
                    size="18"
                    class="input-success-icon password-icon"
                  />
                </div>
                <b-form-text
                  id="email-desc"
                  class="sr-only"
                >
                  Champ obligatoire. Saisissez une adresse e-mail professionnelle valide.
                </b-form-text>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Téléphone professionnel *"
              label-for="phone"
              description="Numéro de téléphone mobile professionnel (8 chiffres, sans espaces). Champ obligatoire."
            >
              <validation-provider
                #default="{ errors, valid }"
                name="Téléphone"
                rules="required|tnPhone|asyncPhone"
              >
                <div class="position-relative">
                  <b-form-input
                    id="phone"
                    v-model="form.phone"
                    placeholder="Numéro de téléphone professionnel"
                    type="tel"
                    maxlength="8"
                    :formatter="formatPhone"
                    :state="errors.length > 0 ? false : (valid && form.phone ? true : null)"
                    aria-describedby="phone-desc"
                    autocomplete="tel"
                    class="premium-input"
                    @keypress="allowOnlyNumbers"
                  />
                  <feather-icon
                    v-if="valid && form.phone"
                    icon="CheckCircleIcon"
                    size="18"
                    class="input-success-icon"
                  />
                </div>
                <b-form-text
                  id="phone-desc"
                  class="sr-only"
                >
                  Format : 8 chiffres, sans espaces.
                </b-form-text>
                <small
                  v-if="errors[0]"
                  class="text-danger validation-message-animate"
                >{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Téléphone secondaire"
              label-for="phone2"
              description="Numéro de téléphone secondaire (optionnel)."
            >
              <b-form-input class="premium-input"
                id="phone2"
                v-model="form.phone2"
                placeholder="Numéro de téléphone secondaire (optionnel)"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                aria-describedby="phone2-desc"
                autocomplete="tel"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text
                id="phone2-desc"
                class="sr-only"
              >
                Format : 8 chiffres, sans espaces.
              </b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label-class="form-label-premium"
          label="Adresse postale"
          label-for="address"
          description="Adresse complète du membre du personnel (rue, ville, code postal)."
        >
          <b-form-textarea
            id="address"
            v-model="form.address"
            placeholder="Exemple : 12 rue de la Logistique, 75000 Paris"
            rows="3"
            aria-describedby="address-desc"
            autocomplete="street-address"
          />
          <b-form-text
            id="address-desc"
            class="sr-only"
          >
            Saisissez l’adresse complète, y compris la ville et le code postal.
          </b-form-text>
        </b-form-group>

        <b-form-group label-class="form-label-premium"
          label="Numéro de CIN (optionnel)"
          label-for="cin"
          description="Carte d’Identité Nationale (8 chiffres, sans espaces)."
        >
          <b-form-input class="premium-input"
            id="cin"
            v-model="form.cin"
            placeholder="Numéro de CIN (optionnel)"
            maxlength="8"
            :formatter="formatCIN"
            :state="null"
            aria-describedby="cin-desc"
            autocomplete="off"
            @keypress="allowOnlyNumbers"
          />
          <b-form-text
            id="cin-desc"
            class="sr-only"
          >
            Format : 8 chiffres, sans espaces.
          </b-form-text>

        </b-form-group>

        <!-- Affectation au dépôt -->
        <h5
          id="section-affectation-depot"
          class="mb-2 mt-4"
        >
          <feather-icon
            icon="HomeIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Affectation au dépôt
        </h5>

        <b-form-group label-class="form-label-premium"
          label="Dépôt logistique"
          label-for="repository"
          :invalid-feedback="''"

          aria-required="false"
          description="Sélectionnez le dépôt logistique auquel le membre sera affecté (optionnel)."
        >
          <v-select
            id="repository"
            v-model="form.repository"
            :options="repositories"
            label="name"
            :reduce="repo => repo.id"
            placeholder="Sélectionnez un dépôt logistique (optionnel)"
            :class="[
              'enhanced-select',
              form.repository ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            :loading="repositoriesLoading"
            :disabled="repositoriesLoading"
            aria-describedby="repository-desc"
            aria-required="false"
            @input="onRepositoryChange"
          >
            <template #no-options>
              <span v-if="repositoriesLoading">Chargement des dépôts logistiques...</span>
              <span v-else>Aucun dépôt disponible</span>
            </template>
            <template #selected-option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #114092;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #114092;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #spinner="{ loading }">
              <div
                v-if="loading"
                class="vs__spinner"
                aria-label="Chargement des dépôts logistiques"
              >
                <div class="loading-dots">
                  <div class="dot1" />
                  <div class="dot2" />
                  <div class="dot3" />
                </div>
              </div>
            </template>
          </v-select>
          <b-form-text
            id="repository-desc"
            class="sr-only"
          >
            Champ optionnel. Sélectionnez le dépôt logistique d’affectation si nécessaire.
          </b-form-text>
        </b-form-group>

        <!-- Informations de connexion -->
        <h5
          id="section-infos-connexion"
          class="mb-2 mt-4"
        >
          <feather-icon
            icon="LockIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations de connexion
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Mot de passe *"
              label-for="password"
              aria-required="true"
              description="Mot de passe d’accès à la plateforme (minimum 8 caractères)."
            >
              <validation-provider
                #default="{ errors }"
                name="Mot de passe"
                rules="required|min:8"
                vid="password"
                mode="eager"
              >
                <b-input-group class="input-group-merge">
                  <b-form-input class="premium-input"
                    id="password"
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    :state="null"
                    placeholder="Entrez un mot de passe sécurisé"
                    aria-describedby="password-desc"
                    aria-required="true"
                    autocomplete="new-password"
                  />
                  <b-input-group-append
                    is-text
                    aria-label="Afficher ou masquer le mot de passe"
                    @click="showPassword = !showPassword"
                  >
                    <feather-icon
                      :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                      class="cursor-pointer"
                      aria-hidden="true"
                    />
                  </b-input-group-append>
                </b-input-group>
                <b-form-text
                  id="password-desc"
                  class="sr-only"
                >
                  Champ obligatoire. Le mot de passe doit comporter au moins 8 caractères.
                </b-form-text>
                <small v-if="errors[0]" class="text-danger validation-message-animate">
                  {{ errors[0] }}
                </small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Confirmer le mot de passe *"
              label-for="confirm-password"
              aria-required="true"
              description="Saisissez à nouveau le mot de passe pour confirmation."
            >
              <validation-provider
                #default="{ errors }"
                name="Confirmation du mot de passe"
                rules="required|confirmed:password"
                mode="eager"
              >
                <b-input-group class="input-group-merge">
                  <b-form-input class="premium-input"
                    id="confirm-password"
                    v-model="form.confirm_password"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    :state="null"
                    placeholder="Confirmez le mot de passe"
                    aria-describedby="confirm-password-desc"
                    aria-required="true"
                    autocomplete="new-password"
                  />
                  <b-input-group-append
                    is-text
                    aria-label="Afficher ou masquer la confirmation du mot de passe"
                    @click="showConfirmPassword = !showConfirmPassword"
                  >
                    <feather-icon
                      :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                      class="cursor-pointer"
                      aria-hidden="true"
                    />
                  </b-input-group-append>
                </b-input-group>
                <b-form-text
                  id="confirm-password-desc"
                  class="sr-only"
                >
                  Champ obligatoire. Saisissez à nouveau le mot de passe pour confirmation.
                </b-form-text>
                <small v-if="errors[0]" class="text-danger validation-message-animate">
                  {{ errors[0] }}
                </small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Generate Password Button -->
        <div class="text-center mb-3 mt-2">
          <PasswordGenerator
            :form-data="form"
            user-type="staff"
            @generated="onPasswordGenerated"
          />
        </div>

        <!-- Boutons d’action -->
        <div
          class="d-flex justify-content-between mt-4"
          role="group"
          aria-label="Actions du formulaire"
        >
          <b-button
            variant="outline-secondary"
            :to="{ name: 'staff-list' }"
            aria-label="Annuler et revenir à la liste du personnel"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
              aria-hidden="true"
            />
            Annuler
          </b-button>
          <span
            id="staff-create-save-btn-wrapper"
            class="d-inline-block"
          >
            <b-button
              variant="primary"
              type="submit"
              :disabled="isSubmitting"
              :class="{ 'not-allowed': isSubmitting }"
              aria-label="Enregistrer le membre du personnel"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
                aria-label="Enregistrement en cours"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-1"
                aria-hidden="true"
              />
              Enregistrer
            </b-button>
            <b-tooltip
              v-if="isSubmitting"
              target="staff-create-save-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              {{ saveButtonTooltipMessage }}
            </b-tooltip>
          </span>
        </div>
      </b-form>
      </ValidationObserver>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BFormText,
  BTooltip,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'

export default {
  name: 'StaffCreate',

  components: {
    PasswordGenerator,
    BCard,
    ValidationObserver,
    ValidationProvider,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BFormText,
    BTooltip,
    vSelect,
  },

  data() {
    return {
      isSubmitting: false,
      repositories: [],
      repositoriesLoading: true,
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        cin: '',
        password: '',
        confirm_password: '',
        repository: null,
        role: null, // Add role for staff
        can_receive_commission: false,
        commission_rate: '',
        commission_start_date: '',
        commission_end_date: '',
      },
      showPassword: false,
      showConfirmPassword: false,
      staffRoles: [
        { label: 'Commercial', value: 'sales' },
        { label: 'Magasinier', value: 'warehouse_operator' },
        { label: 'Support Client', value: 'customer_support' },
        { label: 'Comptable', value: 'accountant' },
      ],
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.form.username && this.form.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du staff'
    },

    saveButtonTooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'

      // Basic check for required fields (VeeValidate handles detailed validation)
      const missing = []

      if (!this.form.role) missing.push('Rôle')
      if (!this.form.username || !this.form.username.trim()) missing.push('Nom complet')
      if (!this.form.email || !this.form.email.trim()) missing.push('Email')
      if (!this.form.phone || !this.form.phone.trim()) missing.push('Téléphone professionnel')
      if (!this.form.password) missing.push('Mot de passe')
      if (!this.form.confirm_password) missing.push('Confirmation du mot de passe')

      // Commission validation messages
      if (this.form.can_receive_commission) {
        const commissionRate = parseFloat(this.form.commission_rate)
        if (!this.form.commission_rate || Number.isNaN(commissionRate) || commissionRate <= 0) {
          missing.push('Taux de commission')
        }
        if (!this.form.commission_start_date) missing.push('Date de début de commission')
        if (!this.form.commission_end_date) missing.push('Date de fin de commission')
      }

      if (missing.length > 0) {
        if (missing.length === 1) {
          return `Veuillez renseigner le champ : ${missing[0]}.`
        }
        if (missing.length <= 3) {
          return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
        }
        return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
      }

      return 'Veuillez corriger les erreurs dans le formulaire.'
    },
  },

  created() {
    this.fetchRepositories()
  },

  mounted() {
    // Ensure proper CSS loading after component mount
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  // Handle route changes for proper CSS reloading
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$nextTick(() => {
        vm.refreshSelectStyles()
      })
    })
  },

  activated() {
    // For keep-alive components
    this.$nextTick(() => {
      this.refreshSelectStyles()
    })
  },

  methods: {
    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    onPasswordGenerated(generatedPassword) {
      this.form.password = generatedPassword
      this.form.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    generateCompanyEmail() {
      if (!this.form.username || !this.form.username.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Attention',
            icon: 'AlertCircleIcon',
            text: 'Veuillez d\'abord saisir le nom du staff.',
            variant: 'warning',
          },
        })
        return
      }

      const username = this.form.username.trim()
      const cleanUsername = this.stripAccents(username)
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9.]/g, '')

      const generatedEmail = `${cleanUsername}@${this.companyDomain}`
      this.form.email = generatedEmail

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Email généré',
          icon: 'CheckCircleIcon',
          text: `Email: ${generatedEmail}`,
          variant: 'success',
        },
      })
    },

    refreshSelectStyles() {
      // Force refresh of v-select styles
      const selectElements = this.$el.querySelectorAll('.enhanced-select')
      selectElements.forEach(element => {
        // Trigger a reflow to ensure styles are applied
        const originalDisplay = element.style.display
        // eslint-disable-next-line no-param-reassign
        element.style.display = 'none'
        // eslint-disable-next-line no-unused-expressions
        element.offsetHeight // trigger reflow
        // eslint-disable-next-line no-param-reassign
        element.style.display = originalDisplay || ''
      })
    },
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }

      // Also prevent input if already at 8 digits
      if (target.id === 'phone' && this.form.phone && this.form.phone.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'phone2' && this.form.phone2 && this.form.phone2.length >= 8) {
        event.preventDefault()
      }

      if (target.id === 'cin' && this.form.cin && this.form.cin.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    formatCIN(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    allowOnlyDecimalNumbers(event) {
      const { charCode } = event
      const currentValue = event.target.value || ''

      // Allow digits (0-9)
      if (charCode >= 48 && charCode <= 57) {
        return true
      }

      // Allow decimal point (46 = '.')
      if (charCode === 46) {
        // Only allow one decimal point
        if (currentValue.includes('.')) {
          event.preventDefault()
          return false
        }
        return true
      }

      // Block all other characters
      event.preventDefault()
      return false
    },

    formatPrice(value) {
      // Remove all non-numeric and non-decimal characters
      let formatted = value.replace(/[^\d.]/g, '')

      // Ensure only one decimal point
      const parts = formatted.split('.')
      if (parts.length > 2) {
        formatted = `${parts[0]}.${parts.slice(1).join('')}`
      }

      // Limit to 3 decimal places
      if (parts.length === 2) {
        formatted = `${parts[0]}.${parts[1].substring(0, 3)}`
      }

      return formatted
    },

    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data

        // Ensure styles are applied after data loads
        this.$nextTick(() => {
          this.refreshSelectStyles()
        })
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les dépôts',
        })
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      } finally {
        this.repositoriesLoading = false
      }
    },

    onRepositoryChange(value) {
      // Handle repository selection change
      this.form.repository = value
      // Clear submitted state for this field if it's now valid
      if (value && this.submitted) {
        this.submitted = false
        this.$nextTick(() => {
          this.submitted = true
        })
      }
    },

    async saveStaff() {
      if (this.isSubmitting) return

      // Use ValidationObserver to validate all fields
      const isValid = await this.$refs.staffFormObserver.validate()
      if (!isValid) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Veuillez corriger les erreurs dans le formulaire',
        })
        return
      }

      this.isSubmitting = true
      try {
        const payload = {
          ...this.form,
          repository: this.form.repository,
          role: this.form.role,
        }
        if (!this.form.can_receive_commission) {
          delete payload.commission_rate
          delete payload.commission_start_date
          delete payload.commission_end_date
        }
        await axios.post('/api/auth/staff-members/create/', payload)
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Membre du personnel créé avec succès',
        })
        setTimeout(() => {
          this.$router.push({ name: 'staff-list' })
        }, 1000)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating staff member:', error)

        // Extract meaningful error message from backend response
        let errorMessage = 'Erreur lors de la création du membre du personnel'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          // Check for specific field errors (like email, phone, username)
          if (errorData.email) {
            const rawError = Array.isArray(errorData.email)
              ? errorData.email[0]
              : errorData.email
            // Transform technical message to user-friendly French
            if (rawError.includes('email') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Membre du personnel avec cet email existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.phone) {
            const rawError = Array.isArray(errorData.phone)
              ? errorData.phone[0]
              : errorData.phone
            if (rawError.includes('phone') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Membre du personnel avec ce téléphone existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.username) {
            const rawError = Array.isArray(errorData.username)
              ? errorData.username[0]
              : errorData.username
            if (rawError.includes('username') && (rawError.includes('existe') || rawError.includes('already exists'))) {
              errorMessage = 'Membre du personnel avec ce nom existe déjà'
            } else {
              errorMessage = rawError
            }
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.error) {
            errorMessage = errorData.error
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          } else {
            // If there are multiple field errors, show the first one
            const firstErrorKey = Object.keys(errorData)[0]
            if (firstErrorKey && errorData[firstErrorKey]) {
              const firstError = errorData[firstErrorKey]
              const rawError = Array.isArray(firstError)
                ? firstError[0]
                : firstError

              // Transform based on field name
              if (firstErrorKey === 'email' && (rawError.includes('existe') || rawError.includes('already exists'))) {
                errorMessage = 'Membre du personnel avec cet email existe déjà'
              } else if (firstErrorKey === 'phone' && (rawError.includes('existe') || rawError.includes('already exists'))) {
                errorMessage = 'Membre du personnel avec ce téléphone existe déjà'
              } else if (firstErrorKey === 'username' && (rawError.includes('existe') || rawError.includes('already exists'))) {
                errorMessage = 'Membre du personnel avec ce nom existe déjà'
              } else {
                errorMessage = rawError
              }
            }
          }
        }

        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: errorMessage,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    showToast({ variant, title, text }) {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'danger') {
        icon = 'AlertTriangleIcon'
      } else {
        icon = 'AlertCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
    // Placeholder for future role change logic
    onRoleChange() {
      if (this.form.role !== 'sales') {
        this.form.commission_rate = ''
        this.form.commission_start_date = ''
        this.form.commission_end_date = ''
        this.form.can_receive_commission = false
      } else {
        this.form.can_receive_commission = true
        if (!this.form.commission_start_date) {
          this.form.commission_start_date = this.getTodayDateString()
        }
        if (!this.form.commission_end_date) {
          this.form.commission_end_date = this.getNextYearDateString()
        }
      }
    },
    onCommissionEligibilityChange() {
      if (this.form.can_receive_commission) {
        if (!this.form.commission_start_date) {
          this.form.commission_start_date = this.getTodayDateString()
        }
        if (!this.form.commission_end_date) {
          this.form.commission_end_date = this.getNextYearDateString()
        }
      } else {
        this.form.commission_rate = ''
        this.form.commission_start_date = ''
        this.form.commission_end_date = ''
      }
    },
    getTodayDateString() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
    getNextYearDateString() {
      const nextYear = new Date()
      nextYear.setFullYear(nextYear.getFullYear() + 1)
      return nextYear.toISOString().split('T')[0]
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.form-group {
  margin-bottom: 1.5rem;
}

.form-control:focus,
.custom-select:focus {
  border-color: #114092;
  box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1);
}

/* Enhanced select dropdown styling - global version for better reliability */
.enhanced-select {
  .vs__dropdown-toggle {
    padding: 6px 12px !important;
    border: 1px solid #d8d6de !important;
    border-radius: 0.357rem !important;
    background-color: #fff !important;
    transition: all 0.3s ease-in-out !important;
    min-height: 42px !important;

    &:hover {
      border-color: #b8b6c0 !important;
    }
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
    background-color: #fff !important;
  }

  &.vs--focused .vs__dropdown-toggle {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
  }

  .vs__selected {
    margin: 2px 2px !important;
    padding: 4px 8px !important;
    color: #5e5873 !important;
    background-color: rgba(17, 64, 146, 0.1) !important;
    border-radius: 4px !important;
    font-weight: 500 !important;
    display: flex !important;
    align-items: center !important;
  }

  .vs__selected-options {
    padding: 2px !important;
    flex-wrap: wrap !important;
  }

  .vs__actions {
    padding-right: 8px !important;
  }  .vs__clear {
    color: #b9b9c3 !important;

    &:hover {
      color: #114092 !important;
    }
  }

  .vs__open-indicator {
    color: #b9b9c3 !important;
    transition: transform 0.3s ease !important;

    &:hover {
      color: #114092 !important;
    }
  }

  &.vs--open .vs__open-indicator {
    transform: rotate(180deg) !important;
  }

  .vs__search {
    padding: 4px 0 !important;
    margin: 0 !important;

    &::placeholder {
      color: #b9b9c3 !important;
    }

    &:focus {
      outline: none !important;
    }
  }

  .vs__dropdown-menu {
    padding: 0 !important;
    border: 1px solid #d8d6de !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
    border-radius: 0.357rem !important;
    background: #fff !important;
    z-index: 1050 !important;
    max-height: 200px !important;
    overflow-y: auto !important;
  }

  .vs__dropdown-option {
    padding: 12px 20px !important;
    color: #5e5873 !important;
    transition: all 0.2s ease !important;
    cursor: pointer !important;
    border-bottom: 1px solid #f0f0f0 !important;

    &:last-child {
      border-bottom: none !important;
    }

    &:hover, &--highlight {
      background: linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%) !important;
      color: #114092 !important;
    }

    &--selected {
      background: linear-gradient(135deg, #114092 0%, #0a2a61 100%) !important;
      color: #fff !important;
      font-weight: 600 !important;
    }

    &--disabled {
      background: #f5f5f5 !important;
      color: #b9b9c3 !important;
      cursor: not-allowed !important;
    }
  }

  .vs__no-options {
    padding: 20px !important;
    text-align: center !important;
    color: #b9b9c3 !important;
    font-style: italic !important;
  }

  .vs__spinner {
    border-color: #114092 transparent transparent !important;
  }

  /* Focus state enhancement */
  &:focus-within .vs__dropdown-toggle {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
  }

  /* Error state styling */
  &.is-invalid .vs__dropdown-toggle {
    border-color: #ea5455 !important;

    &:focus, &:hover {
      border-color: #ea5455 !important;
      box-shadow: 0 3px 10px 0 rgba(234, 84, 85, 0.1) !important;
    }
  }

  /* Success state styling */
  &.is-valid .vs__dropdown-toggle {
    border-color: #28c76f !important;

    &:focus, &:hover {
      border-color: #28c76f !important;
      box-shadow: 0 3px 10px 0 rgba(40, 199, 111, 0.1) !important;
    }
  }
}

/* Ensure proper z-index for dropdowns */
.vs__dropdown-menu {
  z-index: 1050 !important;
}

/* Form enhancements */
.card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
  border: none !important;
  border-radius: 0.75rem !important;
}

.card-title {
  font-weight: 700 !important;
  color: #5e5873 !important;
}

h5 {
  color: #5e5873 !important;
  font-weight: 600 !important;
  margin-bottom: 1rem !important;

  .feather {
    color: #114092 !important;
  }
}

/* Form labels styling */
.form-group label {
  font-weight: 600 !important;
  color: #5e5873 !important;
  margin-bottom: 0.5rem !important;
}

/* Form controls enhanced styling */
.form-control {
  border: 1px solid #d8d6de !important;
  border-radius: 0.357rem !important;
  padding: 0.75rem 1rem !important;
  transition: all 0.3s ease !important;

  &:focus {
    border-color: #114092 !important;
    box-shadow: 0 3px 10px 0 rgba(17, 64, 146, 0.1) !important;
  }

  &:hover {
    border-color: #b8b6c0 !important;
  }
}

/* Button styling */
.btn-primary {
  background: linear-gradient(135deg, #114092 0%, #0a2a61 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(17, 64, 146, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(17, 64, 146, 0.4) !important;
  }
}

.btn-outline-secondary {
  border-color: #d8d6de !important;
  color: #5e5873 !important;

  &:hover {
    background-color: #f8f8f8 !important;
    border-color: #b8b6c0 !important;
    color: #5e5873 !important;
  }
}

/* Force CSS re-application after navigation */
.page-enter-active, .page-leave-active {
  transition: opacity 0.3s;
}

.page-enter, .page-leave-to {
  opacity: 0;
}

/* Ensure v-select is always properly styled */
.enhanced-select * {
  box-sizing: border-box !important;
}

/* Fix for navigation CSS loading issues */
.router-view {
  transition: all 0.3s ease;
}

/* Loading state for repository select */
.enhanced-select.vs--loading .vs__open-indicator {
  animation: spin 1s linear infinite !important;
}

.enhanced-select .vs__spinner {
  .loading-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;

    div {
      width: 4px;
      height: 4px;
      margin: 0 2px;
      background-color: #114092;
      border-radius: 50%;
      animation: loading-bounce 1.4s ease-in-out infinite both;
    }

    .dot1 { animation-delay: -0.32s; }
    .dot2 { animation-delay: -0.16s; }
    .dot3 { animation-delay: 0s; }
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhancement for better visual feedback */
.form-group.has-error .enhanced-select .vs__dropdown-toggle {
  border-color: #ea5455 !important;
  animation: shake 0.5s ease-in-out !important;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* Ensure proper z-index stacking */
.enhanced-select .vs__dropdown-menu {
  z-index: 9999 !important;
  position: absolute !important;
}

/* Fix for modal/overlay issues */
.modal .enhanced-select .vs__dropdown-menu {
  z-index: 1060 !important;
}

/* ==========================================
   Real-Time Validation Styles (CTO Standard)
   ========================================== */

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important; /* Allow tooltip hover */
}

/* Tooltip Styling */
.tooltip {
  z-index: 9999 !important;
}

.tooltip-inner {
  max-width: 300px !important;
  text-align: left !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #0d4399; /* Brand color */
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem; /* Space for checkmark */
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Success Icon Position */
.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;
}

.password-icon {
  right: 3.5rem; /* Adjust for password toggle icon */
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

/* Animated Error Messages */
.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Focus within wrapper for better UX */
.position-relative:focus-within .premium-input {
  border-color: #0d4399;
  box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
}
</style>
